"use client";

import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import gql from "graphql-tag";
import type { GeneratedQuery } from "@aws-amplify/api-graphql";
import type { PatientJourneySummary } from "@rmh/defs";
import { PatientJourneySummaryKind } from "@rmh/defs";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";
import { patientJourneySummaryQueryKey } from "./keys";

interface PatientJourneySummaryQueryVariables {
  kind: PatientJourneySummaryKind;
  ref: string;
}

interface PatientJourneySummaryQueryData {
  patientJourneySummary?: PatientJourneySummary | null;
}

export const patientJourneySummaryQuery = gql`
  query patientJourneySummary(
    $kind: PatientJourneySummaryKind!
    $ref: String!
  ) {
    patientJourneySummary(kind: $kind, ref: $ref) {
      kind
      ref
      headline
      narrative
      findings
      unknowns
    }
  }
`.loc?.source.body as GeneratedQuery<
  PatientJourneySummaryQueryVariables,
  PatientJourneySummaryQueryData
>;

/** One record named by the kind that says how to read it. */
export interface PatientJourneySummaryRecord {
  kind: PatientJourneySummaryKind;
  ref: string;
}

/**
 * One record's drill-down. Shared by the row that reads a record on sight and the
 * row that waits to be opened, so both hit the same cache entry: a record read
 * with the report is never re-read when its row is expanded.
 */
const summaryQueryOptions = ({
  kind,
  ref,
}: {
  kind?: PatientJourneySummaryKind | null;
  ref?: string | null;
}) => ({
  queryKey: patientJourneySummaryQueryKey(kind ?? null, ref ?? null),
  queryFn: async (): Promise<PatientJourneySummary> => {
    if (!kind || !ref) {
      throw new Error("There is no record to summarise.");
    }

    const response = await fetchGraphQLClient(patientJourneySummaryQuery, {
      kind,
      ref,
    });
    const summary = response.data?.patientJourneySummary;

    if (!summary) {
      throw new Error("The Patient Journey drill-down returned no summary.");
    }

    return summary;
  },
  staleTime: Infinity,
  gcTime: Infinity,
  retry: false,
});

interface UsePatientJourneySummaryOptions {
  kind?: PatientJourneySummaryKind | null;
  ref?: string | null;
  /** False on a row whose record the report already read for it. */
  enabled?: boolean;
}

export const usePatientJourneySummary = ({
  kind,
  ref,
  enabled = true,
}: UsePatientJourneySummaryOptions) => {
  return useQuery<PatientJourneySummary>({
    ...summaryQueryOptions({ kind, ref }),
    enabled: enabled && Boolean(kind) && Boolean(ref),
  });
};

/**
 * The records the report reads back and explains on its own, each in a request of
 * its own.
 *
 * One per request rather than one request for all of them: each costs a record
 * read and a model call, and AppSync gives every query the same 30-second window —
 * batching them would spend one window on all of them and drop the lot when the
 * slowest overran it.
 *
 * The read list is held by value, not by identity: `useQueries` re-runs its effect
 * whenever the array it is given is a new one, and that effect notifies its
 * subscribers — so a caller that rebuilds the list every render renders forever.
 * Held here rather than asked of every caller, since forgetting it does not
 * degrade, it hangs the page.
 */
export const usePatientJourneySummaries = (
  records: PatientJourneySummaryRecord[]
) => {
  const signature = records
    .map(({ kind, ref }) => `${kind}\u0000${ref}`)
    .join("|");

  // Rebuilt from the signature rather than read off `records`, so the dependency
  // is the read list by value: the same kinds and refs in the same order are the
  // same list however the caller holds them.
  const queries = useMemo(
    () =>
      signature
        ? signature.split("|").map((entry) => {
            const [kind, ref] = entry.split("\u0000");
            return summaryQueryOptions({
              kind: kind as PatientJourneySummaryKind,
              ref,
            });
          })
        : [],
    [signature]
  );

  return useQueries({ queries });
};
